import React from 'react';
import { AnimatedCounter, AnimatedProgress } from '../ui';
import { formatDate, daysUntil } from './checkout';

/* ==================================================================
   EntitlementCards
   ------------------------------------------------------------------
   "Profile Access" and "Job Posts" — the recruiter's live credit
   position, on the dashboard and on the billing page.

   EVERY figure here is passed in from /api/billing/entitlements.
   Nothing is assumed, defaulted to a plausible-looking number, or
   derived from local state — an entitlement panel that showed a
   stale or optimistic balance would be worse than showing nothing.
   ================================================================== */

function UsersIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  );
}

function BriefcaseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
  );
}

/** The status pill: what state is this entitlement actually in? */
function StatusPill({ ent, hasFree }) {
  if (ent.expired) {
    return <span className="bl-pill bl-pill--expired">Expired</span>;
  }
  if (ent.credits_total > 0 && ent.active) {
    const days = daysUntil(ent.expires_at);
    return (
      <span className={`bl-pill ${days !== null && days <= 7 ? 'bl-pill--expired' : 'bl-pill--active'}`}>
        {days === 0 ? 'Expires today' : days === 1 ? '1 day left' : `${days} days left`}
      </span>
    );
  }
  if (hasFree) return <span className="bl-pill bl-pill--free">Free trial</span>;
  return <span className="bl-pill bl-pill--none">No credits</span>;
}

function EntitlementCard({
  ent,
  product,
  onBuy,
  onPrimary,
  primaryLabel,
  loading,
}) {
  if (loading || !ent) {
    return <div className="ui-skel" style={{ height: 186, borderRadius: 12 }} />;
  }

  const isJob = product === 'job';
  const freeRemaining = ent.free_remaining || 0;
  const hasFree = !isJob && freeRemaining > 0;

  // Spendable = paid credits inside a live window, plus any free trial left.
  // `credits_remaining` is already zeroed by the server once the window shuts,
  // so an expired package cannot inflate this.
  const available = (ent.credits_remaining || 0) + freeRemaining;
  const purchased = (ent.credits_total || 0) + (ent.free_total || 0);
  const used = (ent.credits_used || 0) + (ent.free_used || 0);

  const empty = purchased === 0;
  const pct = purchased > 0 ? Math.round((used / purchased) * 100) : 0;

  return (
    <div className={`bl-ent${ent.expired ? ' bl-ent--expired' : ''}${empty ? ' bl-ent--empty' : ''}`}>
      <div className="bl-ent-top">
        <span className="bl-ent-label">
          <span className={`bl-ent-icon${isJob ? ' bl-ent-icon--job' : ''}`} aria-hidden="true">
            {isJob ? <BriefcaseIcon /> : <UsersIcon />}
          </span>
          {isJob ? 'Job Posts' : 'Profile Access'}
        </span>
        <StatusPill ent={ent} hasFree={hasFree} />
      </div>

      <div>
        <div className="bl-ent-count">
          <span className="bl-ent-big">
            <AnimatedCounter value={available} />
          </span>
          {purchased > 0 && <span className="bl-ent-of">of {purchased}</span>}
        </div>
        <p className="bl-ent-caption">
          {isJob
            ? `${available === 1 ? 'job post' : 'job posts'} remaining`
            : `${available === 1 ? 'profile' : 'profiles'} remaining`}
        </p>
      </div>

      {purchased > 0 && (
        <div className="bl-ent-meter">
          <AnimatedProgress value={pct} />
        </div>
      )}

      <div className="bl-ent-meta">
        {ent.current_package && (
          <span>Plan: <strong>{ent.current_package.name}</strong></span>
        )}
        {ent.expires_at && (
          <span>
            {ent.expired ? 'Expired' : 'Valid until'}{' '}
            <strong>{formatDate(ent.expires_at)}</strong>
          </span>
        )}
        {hasFree && ent.credits_total === 0 && (
          <span><strong>{freeRemaining}</strong> free {freeRemaining === 1 ? 'unlock' : 'unlocks'} left</span>
        )}
      </div>

      {/* Credits that survived their window are called out rather than quietly
          dropped from the count — a recruiter who paid for 25 and can spend 0
          deserves to be told why. */}
      {ent.credits_stranded > 0 && (
        <p className="bl-ent-caption" style={{ color: '#92400E' }}>
          {ent.credits_stranded} unused {ent.credits_stranded === 1 ? 'credit' : 'credits'} expired with this package.
        </p>
      )}

      <div className="bl-ent-actions">
        {onPrimary && available > 0 && (
          <button type="button" className="bl-plan-cta" style={{ width: 'auto' }} onClick={onPrimary}>
            {primaryLabel}
          </button>
        )}
        <button
          type="button"
          className="bl-plan-cta bl-plan-cta--ghost"
          style={{ width: 'auto' }}
          onClick={() => onBuy(product)}
        >
          {empty
            ? (isJob ? 'Get Job Posts' : 'Get Profiles')
            : (isJob ? 'Buy More Job Posts' : 'Buy More Profiles')}
        </button>
      </div>
    </div>
  );
}

export default function EntitlementCards({
  entitlements,
  loading = false,
  onBuy,
  onFindStudents,
  onPostJob,
}) {
  return (
    <div className="bl-ent-grid">
      <EntitlementCard
        ent={entitlements?.profile}
        product="profile"
        loading={loading}
        onBuy={onBuy}
        onPrimary={onFindStudents}
        primaryLabel="Find Students"
      />
      <EntitlementCard
        ent={entitlements?.job}
        product="job"
        loading={loading}
        onBuy={onBuy}
        onPrimary={onPostJob}
        primaryLabel="Post a Job"
      />
    </div>
  );
}
